<template>
  <div v-if="isOpen" class="establishments-modal">
    <div class="modal-overlay" @click="closeModal"></div>
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ dynamicTitle }}</h2>
        <button class="close-button" @click="closeModal">
          <font-awesome-icon icon="times" />
        </button>
      </div>
      
      <div class="sentiment-filters">
        <button 
          v-for="(filter, key) in sentimentFilters" 
          :key="key"
          :class="['filter-button', { active: activeSentimentFilter === key }, key]"
          @click="activeSentimentFilter = key"
        >
          {{ filter.label }}
          <span class="count">({{ getFilteredCount(key) }})</span>
        </button>
      </div>
      
      <!-- Nova seção de filtros com design melhorado -->
      <div class="sorting-dashboard">
        <div class="sorting-title">
          <font-awesome-icon icon="sort" class="sort-icon" />
          <h3>Ordenação Avançada</h3>
        </div>
        
        <div class="sorting-options">
          <div 
            v-for="(option, key) in orderOptions" 
            :key="key"
            :class="['sort-option', { active: activeOrderBy === key }, key]"
            @click="setOrderBy(key)"
            :title="option.tooltip"
          >
            <div class="sort-option-header">
              <span class="sort-label">
                <font-awesome-icon :icon="getOptionIcon(key)" />
                {{ option.label }}
              </span>
              <div class="sort-controls" v-if="activeOrderBy === key">
                <button 
                  :class="['direction-btn', { active: orderDirection === 'asc' }]"
                  @click.stop="setDirection('asc')"
                  title="Ordenar ascendente (A-Z, menor-maior)"
                >
                  <font-awesome-icon icon="arrow-up" />
                </button>
                <button 
                  :class="['direction-btn', { active: orderDirection === 'desc' }]"
                  @click.stop="setDirection('desc')"
                  title="Ordenar descendente (Z-A, maior-menor)"
                >
                  <font-awesome-icon icon="arrow-down" />
                </button>
              </div>
            </div>
            <div class="sort-progress-bar" v-if="activeOrderBy === key"></div>
          </div>
          
          <button 
            class="reset-sort-btn" 
            @click="resetOrdering"
            v-if="activeOrderBy !== null"
            title="Limpar ordenação e restaurar ordem original"
          >
            <font-awesome-icon icon="sync-alt" class="reset-icon" />
            <span>Limpar Ordenação</span>
          </button>
        </div>
      </div>

      <div class="establishments-grid">
        <div 
          v-for="(establishment, index) in orderedEstablishments" 
          :key="`${establishment.id || establishment.place_id}-${activeSentimentFilter}-${index}`"
          class="establishment-card"
          :class="getSentimentClass(establishment.rating)"
        >
          <div class="card-header">
            <div class="rating-circle" :class="getSentimentClass(establishment.rating)">
              {{ establishment.rating.toFixed(1) }}
            </div>
            <div class="review-count">
              <font-awesome-icon icon="star" />
              {{ establishment.review_count }} avaliações
            </div>
          </div>
          
          <h3 class="establishment-name">{{ establishment.name }}</h3>
          <p class="establishment-address">{{ establishment.formatted_address }}</p>
          
          <div class="card-footer">
            <div class="coordinates">
              <font-awesome-icon icon="map-marker-alt" />
              {{ formatCoordinates(establishment.location) }}
            </div>
            <button class="view-details" @click="viewDetails(establishment)">
              <span class="button-content">
                <font-awesome-icon icon="search" class="icon" />
                <span class="text">Ver Detalhes</span>
                <span class="arrow">→</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal de detalhes do estabelecimento -->
    <establishment-details-modal
      v-if="showDetailsModal"
      :is-visible="showDetailsModal"
      :establishment="selectedEstablishment"
      :category="selectedCategory"
      @close="closeDetailsModal"
    />
  </div>
</template>

<script>
import EstablishmentDetailsModal from './EstablishmentDetailsModal.vue'

export default {
  name: 'EstablishmentsModal',
  components: {
    EstablishmentDetailsModal
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    selectedCategory: {
      type: String,
      required: true,
      default: 'positive'
    },
    establishmentsData: {
      type: Object,
      required: true
    },
    categoryFilter: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      activeSentimentFilter: this.selectedCategory,
      orderDirection: 'asc',
      activeOrderBy: null,
      showDetailsModal: false,
      selectedEstablishment: null,
      sentimentFilters: {
        positive: { label: 'Positivos', filter: (e) => e.rating >= 4 },
        neutral: { label: 'Neutros', filter: (e) => e.rating >= 2 && e.rating < 4 },
        negative: { label: 'Negativos', filter: (e) => e.rating < 2 }
      },
      orderOptions: {
        name: { 
          label: 'Nome',
          tooltip: 'Ordena alfabeticamente pelo nome do estabelecimento'
        },
        rating: { 
          label: 'Avaliação / Estrelas',
          tooltip: 'Ordena pela pontuação média em estrelas (1-5)'
        },
        reviews: { 
          label: 'Quantidade de Avaliações',
          tooltip: 'Ordena pelo número total de avaliações recebidas' 
        },
        popularity: { 
          label: 'Popularidade',
          tooltip: 'Combina nota e quantidade de avaliações para classificar por relevância'
        },
        trend: { 
          label: 'Tendência',
          tooltip: 'Mostra primeiro estabelecimentos com avaliações em alta'
        },
        address: { 
          label: 'Endereço / CEP',
          tooltip: 'Ordena pelo endereço físico do estabelecimento'
        },
        date: { 
          label: 'Data das Avaliações',
          tooltip: 'Ordena pela data das avaliações (mais antigas ou mais recentes)'
        }
      },
      sortBy: 'rating',
      sortOrder: 'asc',
      reviewsStore: new Map(), // Armazena os reviews originais por ID do estabelecimento
    }
  },
  computed: {
    dynamicTitle() {
      let baseTitle = "";
      const sentimentTitles = {
        positive: 'Avaliações Positivas',
        neutral: 'Avaliações Neutras',
        negative: 'Avaliações Negativas'
      };
      
      baseTitle = sentimentTitles[this.activeSentimentFilter] || sentimentTitles.positive;
      
      if (this.categoryFilter) {
        baseTitle += ` - ${this.getCategoryDisplayName(this.categoryFilter)}`;
      }
      
      return baseTitle;
    },
    filteredEstablishments() {
      return this.establishmentsData[this.activeSentimentFilter] || []
    },
    orderedEstablishments() {
      // Se não houver ordenação ativa, retornar apenas os estabelecimentos filtrados
      if (!this.activeOrderBy) {
        return this.filteredEstablishments
      }
      
      // Clonamos o array para evitar modificações diretas no original
      const establishments = [...this.filteredEstablishments]
      
      // Ordenamos com base no campo e direção selecionados
      return this.sortEstablishments(establishments, this.activeOrderBy, this.orderDirection)
    }
  },
  mounted() {
    // Inicializa o filtro com a categoria selecionada ou padrão positivo
    this.activeSentimentFilter = this.selectedCategory || 'positive';
    
    // Corrige o bug dos controles de zoom do mapa
    this.fixLeafletControls()
  },
  beforeDestroy() {
    // Certifica-se de restaurar os controles antes de destruir o componente
    this.restoreLeafletControls()
  },
  methods: {
    closeModal() {
      // Restaura os controles antes de fechar o modal
      this.restoreLeafletControls()
      this.$emit('close')
    },
    getSentimentClass(rating) {
      if (rating >= 4) return 'positive'
      if (rating >= 2) return 'neutral'
      return 'negative'
    },
    getFilteredCount(filter) {
      return this.establishmentsData[filter]?.length || 0
    },
    formatCoordinates(location) {
      return `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`
    },
    viewDetails(establishment) {
      this.selectedEstablishment = establishment
      this.showDetailsModal = true
    },
    closeDetailsModal() {
      this.showDetailsModal = false
      this.selectedEstablishment = null
    },
    sortEstablishments(establishments, orderBy, direction) {
      // Se não houver estabelecimentos ou critério de ordenação, retornar o array original
      if (!establishments.length || !orderBy) {
        return establishments;
      }

      // Cria uma cópia dos estabelecimentos mantendo os reviews
      const establishmentsToSort = establishments.map(establishment => ({
        ...establishment
      }));

      // Função de ordenação para cada campo
      const sortFunctions = {
        name: (a, b) => {
          return direction === 'asc' 
            ? (a.name || '').localeCompare(b.name || '') 
            : (b.name || '').localeCompare(a.name || '')
        },
        reviews: (a, b) => {
          const reviewsA = a.review_count || 0
          const reviewsB = b.review_count || 0
          return direction === 'asc' ? reviewsA - reviewsB : reviewsB - reviewsA
        },
        address: (a, b) => {
          return direction === 'asc' 
            ? (a.formatted_address || '').localeCompare(b.formatted_address || '') 
            : (b.formatted_address || '').localeCompare(a.formatted_address || '')
        },
        rating: (a, b) => {
          const ratingA = a.rating || 0
          const ratingB = b.rating || 0
          return direction === 'asc' ? ratingA - ratingB : ratingB - ratingA
        },
        popularity: (a, b) => {
          const scoreA = (a.rating || 0) * Math.log(a.review_count + 1)
          const scoreB = (b.rating || 0) * Math.log(b.review_count + 1)
          return direction === 'asc' ? scoreA - scoreB : scoreB - scoreA
        },
        trend: (a, b) => {
          const trendScoreA = ((a.rating || 0) - 2.5) * 2
          const trendScoreB = ((b.rating || 0) - 2.5) * 2
          return direction === 'asc' ? trendScoreA - trendScoreB : trendScoreB - trendScoreA
        },
        date: (a, b) => {
          // Obtém os reviews originais para comparação
          const reviewsA = a.reviews || [];
          const reviewsB = b.reviews || [];

          // Função para obter a data mais recente
          const getLatestDate = (reviews) => {
            if (!reviews || reviews.length === 0) return 0;
            
            return Math.max(...reviews.map(review => 
              new Date(review.time_created).getTime()
            ));
          };

          const dateA = getLatestDate(reviewsA);
          const dateB = getLatestDate(reviewsB);
          
          return direction === 'asc' ? dateA - dateB : dateB - dateA;
        }
      }
      
      // Se a função de ordenação existe, usamos ela
      if (sortFunctions[orderBy]) {
        // Ordenamos o array
        establishmentsToSort.sort(sortFunctions[orderBy]);
        
        return establishmentsToSort;
      }
      
      // Caso contrário, retornamos o array sem alterações
      return establishments;
    },
    setOrderBy(orderKey) {
      if (this.activeOrderBy === orderKey) {
        // Se já estiver ordenando por este campo, inverte a direção
        this.orderDirection = this.orderDirection === 'asc' ? 'desc' : 'asc'
      } else {
        // Caso contrário, define o novo campo e direção padrão
        this.activeOrderBy = orderKey
        this.orderDirection = 'asc'
      }
    },
    setDirection(direction) {
      if (this.orderDirection !== direction) {
        this.orderDirection = direction
      }
    },
    resetOrdering() {
      this.activeOrderBy = null
      this.orderDirection = 'asc'
    },
    // Método para corrigir o bug de controles do Leaflet
    fixLeafletControls() {
      // Armazena os controles de zoom do mapa em uma variável temporária
      const mapControls = document.querySelectorAll('.leaflet-control-container')
      if (mapControls.length > 0) {
        // Adiciona uma classe especial para esconder durante a exibição do modal
        mapControls.forEach(control => {
          control.classList.add('hidden-during-modal')
        })
      }
    },
    // Método para restaurar os controles do mapa
    restoreLeafletControls() {
      const mapControls = document.querySelectorAll('.leaflet-control-container.hidden-during-modal')
      if (mapControls.length > 0) {
        mapControls.forEach(control => {
          control.classList.remove('hidden-during-modal')
        })
      }
    },
    getOptionIcon(key) {
      const icons = {
        name: 'font',
        rating: 'star',
        reviews: 'comment',
        popularity: 'fire',
        trend: 'chart-line',
        address: 'map-marker-alt',
        date: 'calendar'
      }
      return icons[key] || 'sort'
    },
    getCategoryDisplayName(category) {
      const categoryNames = {
        gastronomy: 'Gastronomia',
        lodging: 'Hospedagem',
        tour: 'Atrações Turísticas',
        transport: 'Transporte',
        nightlife: 'Vida Noturna',
        commerce: 'Comércio',
        positive: 'Avaliações Positivas',
        neutral: 'Avaliações Neutras',
        negative: 'Avaliações Negativas'
      }
      return categoryNames[category] || category
    },
    clearCategoryFilter() {
      this.$emit('clear-category-filter')
    },
    // Método para salvar os reviews originais
    storeOriginalReviews(establishments) {
      establishments.forEach(establishment => {
        if (establishment.reviews && establishment.reviews.length > 0) {
          this.reviewsStore.set(establishment.id, [...establishment.reviews]);
        }
      });
    },
    // Método para obter os reviews originais de um estabelecimento
    getOriginalReviews(establishmentId) {
      return this.reviewsStore.get(establishmentId) || [];
    },
    // Método para abrir o modal de detalhes
    openEstablishmentDetails(establishment) {
      // Recupera os reviews originais antes de abrir o modal
      const originalReviews = this.getOriginalReviews(establishment.id);
      
      // Cria uma cópia do estabelecimento com os reviews originais
      const establishmentWithOriginalReviews = {
        ...establishment,
        reviews: originalReviews
      };

      this.$emit('show-details', establishmentWithOriginalReviews);
    },
  },
  created() {
    // Quando o componente é criado, armazena os reviews originais
    if (this.establishments) {
      this.storeOriginalReviews(this.establishments);
    }
  },
  watch: {
    establishments: {
      handler(newEstablishments) {
        // Quando os estabelecimentos mudam, atualiza o store de reviews
        if (newEstablishments) {
          this.storeOriginalReviews(newEstablishments);
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.establishments-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
}

.modal-content {
  position: relative;
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h2 {
    margin: 0;
    font-size: 2rem;
    color: #2c3e50;
  }
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  
  &:hover {
    color: #333;
  }
}

.sentiment-filters {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  justify-content: flex-start;
  padding: 0 1rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 1rem;
}

.filter-button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 20px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
  min-width: 120px;
  text-align: center;
  
  &.active {
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    
    &.positive { 
      background: #0b8c0b;
      color: #fff;
      box-shadow: 0 2px 4px rgba(11, 140, 11, 0.3);
    }
    
    &.neutral { 
      background: #ffc700;
      color: #fff;
      box-shadow: 0 2px 4px rgba(255, 199, 0, 0.3);
    }
    
    &.negative { 
      background: #f25362;
      color: #fff;
      box-shadow: 0 2px 4px rgba(242, 83, 98, 0.3);
    }
  }
  
  .count {
    margin-left: 0.5rem;
    font-size: 0.9em;
    opacity: 0.9;
  }
}

/* Novo design criativo para a seção de ordenação */
.sorting-dashboard {
  background: linear-gradient(to right, #f8f9fa, #e9ecef);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.sorting-title {
  display: flex;
  align-items: center;
  margin-bottom: 1.25rem;
  
  .sort-icon {
    font-size: 1.2rem;
    color: #2c3e50;
    margin-right: 0.75rem;
  }
  
  h3 {
    margin: 0;
    font-size: 1.3rem;
    color: #2c3e50;
    font-weight: 600;
  }
}

.sorting-options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
}

.sort-option {
  background: white;
  border-radius: 10px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 4px;
    background: transparent;
    transition: all 0.3s;
  }
  
  &:hover::before {
    background: #ddd;
  }
  
  &.active {
    border-color: #3498db;
    background: #f8fafe;
    
    &::before {
      background: #3498db;
    }
  }
}

.sort-option-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.sort-label {
  font-weight: 500;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.sort-controls {
  display: flex;
  gap: 0.25rem;
  margin-left: 0.5rem;
}

.direction-btn {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  svg {
    font-size: 0.8rem;
    color: #666;
  }
  
  &:hover {
    background: #e0e0e0;
  }
  
  &.active {
    background: #3498db;
    
    svg {
      color: white;
    }
  }
}

.sort-progress-bar {
  height: 3px;
  width: 100%;
  background: #3498db;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 30%;
    background: rgba(255, 255, 255, 0.3);
    animation: progress 1.5s ease-in-out infinite;
  }
}

@keyframes progress {
  0% {
    left: -30%;
  }
  100% {
    left: 100%;
  }
}

.reset-sort-btn {
  grid-column: 1 / -1;
  background: #f8f9fa;
  border: 1px dashed #dee2e6;
  border-radius: 10px;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.3s;
  color: #6c757d;
  font-weight: 500;
  margin-top: 0.5rem;
  
  .reset-icon {
    font-size: 1rem;
    transition: transform 0.3s;
  }
  
  &:hover {
    background: #e9ecef;
    color: #495057;
    
    .reset-icon {
      transform: rotate(180deg);
    }
  }
}

.establishments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.establishment-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
  }
  
  &.positive {
    border-left: 4px solid #0b8c0b;
  }
  
  &.neutral {
    border-left: 4px solid #ffc700;
  }
  
  &.negative {
    border-left: 4px solid #f25362;
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.rating-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  
  &.positive { background: #0b8c0b; }
  &.neutral { background: #ffc700; }
  &.negative { background: #f25362; }
}

.review-count {
  color: #666;
  font-size: 0.9rem;
  
  svg {
    color: #ffc700;
    margin-right: 0.5rem;
  }
}

.establishment-name {
  margin: 0 0 0.5rem;
  font-size: 1.2rem;
  color: #2c3e50;
}

.establishment-address {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}

.coordinates {
  color: #666;
  font-size: 0.8rem;
  
  svg {
    margin-right: 0.5rem;
  }
}

.view-details {
  background: none;
  border: 1px solid #ddd;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s;
  overflow: hidden;
  
  .button-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .icon {
      font-size: 0.9rem;
      transition: transform 0.3s;
    }
    
    .text {
      font-weight: 500;
    }
    
    .arrow {
      transition: transform 0.3s;
    }
  }
  
  &:hover {
    background: #f5f5f5;
    border-color: #ccc;
    
    .button-content {
      .icon {
        transform: scale(1.1);
      }
      
      .arrow {
        transform: translateX(5px);
      }
    }
  }
}
</style>

<style>
/* Estilos globais para corrigir o bug do Leaflet */
.hidden-during-modal {
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
}
</style> 